import { useState, type FC } from 'react';

interface StudentRequestModalProps {
  isOpen: boolean;
  disciplineCode: string;
  teacherName?: string;
  onClose: () => void;
  onSubmit: (payload: { discipline_code: string; category: string; title: string; message: string }) => Promise<void>;
}

export const StudentRequestModal: FC<StudentRequestModalProps> = ({
  isOpen,
  disciplineCode,
  teacherName,
  onClose,
  onSubmit,
}) => {
  const [category, setCategory] = useState('Вопрос по дисциплине');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        discipline_code: disciplineCode,
        category,
        title: title.trim(),
        message: message.trim(),
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
              ОБРАЩЕНИЕ К ПРЕПОДАВАТЕЛЮ
            </span>
            <h3 className="text-base font-bold text-white mt-0.5">
              {teacherName ? `${teacherName} (${disciplineCode})` : disciplineCode}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3 text-xs">
          <div>
            <label className="block text-slate-300 font-medium mb-1">Категория вопроса</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white focus:border-cyan-400 focus:outline-none"
            >
              <option value="Вопрос по дисциплине">Вопрос по дисциплине</option>
              <option value="Консультация по заданию">Консультация по заданию</option>
              <option value="СРОП">СРОП</option>
              <option value="Вопрос по РК">Вопрос по РК</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-300 font-medium mb-1">Тема обращения *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Краткая формулировка вопроса..."
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white focus:border-cyan-400 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-slate-300 font-medium mb-1">Содержание вопроса *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Подробно опишите вопрос или укажите материал/слайд..."
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white focus:border-cyan-400 focus:outline-none"
            />
          </div>
        </div>

        <div className="pt-3 border-t border-slate-800 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-700 hover:bg-slate-800 text-xs font-semibold text-slate-300"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !title.trim() || !message.trim()}
            className="px-5 py-2 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md disabled:opacity-50"
          >
            {isSubmitting ? 'Отправка...' : 'Отправить обращение'}
          </button>
        </div>
      </div>
    </div>
  );
};
